import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import "@/i18n";

import Index from "./pages/Index";
import AuthPage from "./pages/Auth";
import AboutSPA from "./pages/AboutSPA";
import CompanyDashboard from "./pages/dashboard/CompanyDashboard";
import ProfessionalDashboard from "./pages/dashboard/ProfessionalDashboard";
import AdminDashboard from "./pages/dashboard/AdminDashboard";
import CompanyProfileSetup from "./pages/profile/CompanyProfileSetup";
import CompanyProfile from "./pages/profile/CompanyProfile";
import ProfessionalProfileSetup from "./pages/profile/ProfessionalProfileSetup";
import MyProfile from "./pages/profile/MyProfile";
import OpportunitiesDirectory from "./pages/opportunities/OpportunitiesDirectory";
import OpportunityDetail from "./pages/opportunities/OpportunityDetail";
import CreateOpportunity from "./pages/opportunities/CreateOpportunity";
import CompaniesDirectory from "./pages/companies/CompaniesDirectory";
import SuppliersHub from "./pages/companies/SuppliersHub";
import CompanyDetail from "./pages/companies/CompanyDetail";
import SpaPrograms from "./pages/programs/SpaPrograms";
import DocumentsLibrary from "./pages/documents/DocumentsLibrary";
import EventsDirectory from "./pages/events/EventsDirectory";
import EventDetail from "./pages/events/EventDetail";
import SpaMap from "./pages/map/SpaMap";
import ResetPassword from "./pages/ResetPassword";
import MyOpportunities from "./pages/MyOpportunities";
import MyApplications from "./pages/applications/MyApplications";
import CompanyApplications from "./pages/applications/CompanyApplications";
import TalentDirectory from "./pages/TalentDirectory";
import CompanyBids from "./pages/dashboard/CompanyBids";
import NotFound from "./pages/NotFound";
import ForCompanies from "./pages/ForCompanies";
import ForBusinesses from "./pages/ForBusinesses";
import ForProfessionals from "./pages/ForProfessionals";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/about-spa" element={<AboutSPA />} />
            
            {/* Dashboards */}
            <Route path="/dashboard/company" element={<CompanyDashboard />} />
            <Route path="/dashboard/professional" element={<ProfessionalDashboard />} />
            <Route path="/dashboard/admin" element={<AdminDashboard />} />
            <Route path="/dashboard/company/opportunities" element={<MyOpportunities />} />
            <Route path="/dashboard/company/applications" element={<CompanyApplications />} />
            <Route path="/dashboard/company/bids" element={<CompanyBids />} />
            <Route path="/dashboard/professional/applications" element={<MyApplications />} />
            
            {/* Profile */}
            <Route path="/profile" element={<MyProfile />} />
            <Route path="/profile/company" element={<CompanyProfile />} />
            <Route path="/profile/company/setup" element={<CompanyProfileSetup />} />
            <Route path="/profile/professional/setup" element={<ProfessionalProfileSetup />} />
            
            {/* Opportunities */}
            <Route path="/opportunities" element={<OpportunitiesDirectory />} />
            <Route path="/opportunities/create" element={<CreateOpportunity />} />
            <Route path="/opportunities/:id" element={<OpportunityDetail />} />
            
            {/* Companies */}
            <Route path="/companies" element={<CompaniesDirectory />} />
            <Route path="/companies/:id" element={<CompanyDetail />} />
            <Route path="/suppliers" element={<SuppliersHub />} />
            
            {/* Programs & Documents */}
            <Route path="/programs" element={<SpaPrograms />} />
            <Route path="/documents" element={<DocumentsLibrary />} />
            
            {/* Events */}
            <Route path="/events" element={<EventsDirectory />} />
            <Route path="/events/:id" element={<EventDetail />} />
            
            {/* SPA Map */}
            <Route path="/map" element={<SpaMap />} />
            
            {/* Talent Directory */}
            <Route path="/talent" element={<TalentDirectory />} />
            
            {/* Target Audience Pages */}
            <Route path="/for-companies" element={<ForCompanies />} />
            <Route path="/for-businesses" element={<ForBusinesses />} />
            <Route path="/for-professionals" element={<ForProfessionals />} />
            
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;